import { clsx } from 'clsx'
import { useDropzone } from 'react-dropzone'

import { ACCEPTED_VIDEO_UPLOAD_TYPES } from 'constants/videoUploadTypes'
import { PERCENT_SCALE } from 'utils/units'

interface UploadDropzoneProperties {
  error: string | undefined
  hasUploadPermission: boolean
  isPreparing: boolean
  isUserLoading: boolean
  onFilesDropped: (files: File[]) => void
  selectedFile: File | undefined
  selectedFiles?: File[]
}

const BYTES_PER_KILOBYTE = 1024

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes'
  }

  const kiloBytes = BYTES_PER_KILOBYTE
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const index = Math.floor(Math.log(bytes) / Math.log(kiloBytes))
  return `${Math.round((bytes / Math.pow(kiloBytes, index)) * PERCENT_SCALE) / PERCENT_SCALE} ${sizes[index]}`
}

const getSelectedFilesSummary = (selectedFiles: File[]): { name: string; sizeLabel: string } => {
  if (selectedFiles.length === 1) {
    const [file] = selectedFiles
    return {
      name: file.name,
      sizeLabel: formatFileSize(file.size),
    }
  }

  const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0)
  return {
    name: `${selectedFiles.length} videos selected`,
    sizeLabel: formatFileSize(totalBytes),
  }
}

const getDropzoneContent = (
  hasUploadPermission: boolean,
  isPreparing: boolean,
  isUserLoading: boolean,
  selectedFiles: File[]
) => {
  if (isPreparing) {
    return (
      <div className="dropzone-content">
        <div className="dropzone-upload-progress">
          <div className="loading-spinner loading-spinner--compact" />
          <p className="dropzone-upload-text">Preparing upload...</p>
        </div>
      </div>
    )
  }

  if (selectedFiles.length > 0) {
    const summary = getSelectedFilesSummary(selectedFiles)
    return (
      <div className="dropzone-content">
        <div className="dropzone-file-info">
          <p className="dropzone-file-name">{summary.name}</p>
          <p className="dropzone-file-size">{summary.sizeLabel}</p>
          <p className="dropzone-hint">Drop new files to replace</p>
        </div>
      </div>
    )
  }

  if (!hasUploadPermission && !isUserLoading) {
    return (
      <div className="dropzone-content">
        <p className="dropzone-text">Please sign in to upload files</p>
      </div>
    )
  }

  return (
    <div className="dropzone-content">
      <div className="dropzone-upload-icon-row">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 15V3M12 3L8 7M12 3L16 7"
            stroke="var(--theme-text-color-muted)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 17L2 19C2 20.1046 2.89543 21 4 21L20 21C21.1046 21 22 20.1046 22 19L22 17"
            stroke="var(--theme-text-color-muted)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="dropzone-text">upload videos to get started</p>
      </div>
    </div>
  )
}

export const UploadDropzone = (properties: UploadDropzoneProperties) => {
  /* --- Section 1: Hooks --- */
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    onDrop: properties.onFilesDropped,
    accept: ACCEPTED_VIDEO_UPLOAD_TYPES,
    multiple: true,
    disabled: properties.isPreparing || !properties.hasUploadPermission || properties.isUserLoading,
  })

  /* --- Section 3: Derived Values --- */
  const { error, isPreparing } = properties
  const selectedFiles = properties.selectedFiles ?? (properties.selectedFile ? [properties.selectedFile] : [])
  const dropzoneContent = getDropzoneContent(
    properties.hasUploadPermission,
    properties.isPreparing,
    properties.isUserLoading,
    selectedFiles
  )

  /* --- Section 6: Render --- */
  return (
    <div className="dropzone-container">
      <div
        {...getRootProps()}
        data-testid="upload-dropzone"
        className={clsx('dropzone', isDragActive && 'dropzone-active', isPreparing && 'dropzone-uploading')}
      >
        <input {...getInputProps()} data-testid="upload-dropzone-input" multiple />
        {dropzoneContent}
      </div>
      {error ? <div className="dropzone-error">{error}</div> : undefined}
    </div>
  )
}
